/**
 * Hook compartilhado para inputs de bipagem em coletores Android.
 *
 * Problema: coletores industriais (Zebra, Honeywell, etc) entregam o código via
 * IME (keyboard wedge) — precisam de um <input> focado. Mas focar o input no
 * Android abre o teclado virtual e cobre metade da tela.
 *
 * Solução combinada (3 peças):
 *   1. navigator.virtualKeyboard.hide() — Chrome 94+ Android, reforço
 *   2. inputmode="none" aplicado SÓ no instante do focus(), removido 250ms
 *      depois. Permanente quebra alguns coletores; só-no-foco impede o teclado
 *      sem quebrar a entrega do scanner.
 *   3. Listeners globais de pointerdown/touchstart que re-armam o input —
 *      cobre o caso "toquei na tela e o Android tentou reabrir o teclado".
 *
 * No mobile/coarse-pointer, o input deve ficar VISUALMENTE escondido (mas
 * mantendo capacidade de focar) — use HIDDEN_INPUT_STYLE.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

function getVK() {
  if (typeof navigator === 'undefined') return null
  return navigator.virtualKeyboard || null
}

function hideVirtualKeyboard() {
  const vk = getVK()
  if (!vk) return
  try { vk.overlaysContent = true } catch (_) { /* noop */ }
  try { vk.hide && vk.hide() } catch (_) { /* noop */ }
  // Repete em alguns frames — algumas transições reabrem o teclado depois do hide()
  setTimeout(() => { try { vk.hide && vk.hide() } catch (_) {} }, 50)
  setTimeout(() => { try { vk.hide && vk.hide() } catch (_) {} }, 200)
}

/**
 * "Arma" o input: inputmode=none → focus() → remove inputmode após 250ms.
 * Pode ser chamado livremente; é idempotente e seguro fora de mount.
 */
export function armInput(el) {
  if (!el || !el.isConnected) return
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
  try { el.setAttribute('inputmode', 'none') } catch (_) {}
  hideVirtualKeyboard()
  try { el.focus({ preventScroll: true }) } catch (_) { /* foco indisponível neste instante */ }
  hideVirtualKeyboard()
  setTimeout(() => {
    try { el.removeAttribute('inputmode') } catch (_) {}
    hideVirtualKeyboard()
  }, 250)
}

/**
 * Hook que mantém um input "armado" para receber bipagens sem abrir teclado.
 *
 * @param {object} opts
 * @param {() => HTMLInputElement | null} opts.getInput  função que retorna o
 *        input ATIVO no momento (telas com múltiplos inputs alternam via step).
 * @param {() => boolean} [opts.getDisabled]  retorna true se NÃO deve armar
 *        (ex: modal aberto, auth pendente, busy). Padrão: sempre arma.
 * @param {*} [opts.armKey]  Quando esse valor MUDA, o hook re-arma o input
 *        atual. Use pra disparar re-arm em transições de step/aba/box —
 *        cobre o caso "operador bipou o rastreio, agora precisa bipar o box".
 *        Sem isso, o `.focus()` que a tela faz após `setStep('box')` abre
 *        o teclado virtual porque o novo input não foi armado.
 *
 * Retorna `{ arm }` — chame manualmente ao trocar de step/tab/etc se precisar
 * de garantia extra (handleBlur, closeModal, etc).
 */
export function useScannerInput({ getInput, getDisabled, armKey }) {
  // Refs evitam recriar o handler a cada render (o useEffect rodaria toda hora)
  const getInputRef = useRef(getInput)
  const getDisabledRef = useRef(getDisabled)
  useEffect(() => { getInputRef.current = getInput })
  useEffect(() => { getDisabledRef.current = getDisabled })

  const arm = useCallback(() => {
    if (getDisabledRef.current && getDisabledRef.current()) return
    const el = getInputRef.current && getInputRef.current()
    if (el) armInput(el)
  }, [])

  // Listeners globais — registrados uma vez no mount, mantêm o input
  // armado em mudanças de visibilidade/foco/toque.
  useEffect(() => {
    arm()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') arm()
    }
    const onPointer = () => {
      // Toque pode reabrir o teclado — re-arma no próximo tick (depois do tap default do browser)
      setTimeout(arm, 0)
    }

    window.addEventListener('focus', arm)
    window.addEventListener('pageshow', arm)
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('pointerdown', onPointer, true)
    document.addEventListener('touchstart', onPointer, true)

    return () => {
      window.removeEventListener('focus', arm)
      window.removeEventListener('pageshow', arm)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('pointerdown', onPointer, true)
      document.removeEventListener('touchstart', onPointer, true)
    }
  }, [arm])

  // Re-arma quando o input ATIVO muda (step/activeTab/etc).
  // Sem isso, ao mudar de step a tela faz `.focus()` direto no novo input
  // e o Android abre o teclado — esse hook PRECISA armar antes.
  useEffect(() => {
    // Pequeno atraso pra deixar o React montar/atualizar o input antes do focus()
    const t = setTimeout(arm, 30)
    return () => clearTimeout(t)
  }, [armKey, arm])

  return { arm }
}

/**
 * Retorna `true` em dispositivos com pointer "coarse" (touch / coletor industrial).
 * Usado para decidir esconder o input visualmente.
 */
export function useIsCoarsePointer() {
  const [coarse, setCoarse] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(pointer: coarse)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(pointer: coarse)')
    const onChange = (e) => setCoarse(e.matches)
    if (mql.addEventListener) mql.addEventListener('change', onChange)
    else mql.addListener(onChange)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange)
      else mql.removeListener(onChange)
    }
  }, [])

  return coarse
}

/**
 * Estilo "visually-hidden" para esconder o input no mobile mantendo a
 * capacidade de receber foco programático (essencial pro scanner entregar).
 *
 * NÃO use display:none ou visibility:hidden — esses tornam o elemento
 * non-focusable, e o scanner para de entregar caracteres.
 */
export const HIDDEN_INPUT_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
  opacity: 0,
  pointerEvents: 'none',
}
